import { useCallback, useRef, useState } from 'react';

import { getAllowedExtensionAndMimeSets, validateFiles } from '../file-drop-zone/utils';

const DEFAULT_MAX_SIZE = 20 * 1024 * 1024;

export type UseFileAttachmentOptions = {
  allowedExtensions?: readonly string[];
  maxSizeBytes?: number;
  onFilesChange?: (files: File[]) => void;
  onFileError?: (message: string) => void;
  disabled?: boolean;
  /**
   * Controlled file list. Supply it when the picked files live in the caller's own state (a form field,
   * say) so a removal there can't leave this hook holding a stale copy. Omit it and the hook keeps the
   * list itself, which is the original behavior.
   */
  files?: readonly File[];
  /** Controlled removal. Pair it with `files`; without it a controlled list can only be added to. */
  onRemove?: (index: number) => void;
};

export type UseFileAttachmentResult = {
  files: File[];
  addFiles: (files: File[]) => void;
  removeFile: (index: number) => void;
  openPicker: () => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
};

export function useFileAttachment({
  allowedExtensions,
  maxSizeBytes = DEFAULT_MAX_SIZE,
  onFilesChange,
  onFileError,
  disabled = false,
  files: controlledFiles,
  onRemove,
}: UseFileAttachmentOptions = {}): UseFileAttachmentResult {
  const [internalFiles, setInternalFiles] = useState<File[]>([]);
  const filesRef = useRef<File[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const isControlled = controlledFiles !== undefined;
  const files = isControlled ? [...controlledFiles] : internalFiles;
  // Kept in step so the callbacks below can read the current list without taking it as a dependency.
  filesRef.current = files;

  const addFiles = useCallback(
    (incoming: File[]) => {
      if (disabled) return;

      let accepted: File[];
      let tooLarge: File[] = [];
      let wrongType: File[] = [];

      if (allowedExtensions && allowedExtensions.length > 0) {
        const { allowedExtSet, allowedMimeSet } = getAllowedExtensionAndMimeSets(allowedExtensions);
        const result = validateFiles({ files: incoming, maxSizeBytes, allowedExtSet, allowedMimeSet });
        accepted = result.acceptedFile;
        tooLarge = result.tooLargeFiles;
        wrongType = result.filesOfDisallowedType;
      } else {
        accepted = incoming.filter((f) => f.size <= maxSizeBytes);
        tooLarge = incoming.filter((f) => f.size > maxSizeBytes);
      }

      if (tooLarge.length > 0) {
        onFileError?.(`File too large: ${tooLarge.map((f) => f.name).join(', ')}`);
      }
      if (wrongType.length > 0) {
        onFileError?.(`File type not allowed: ${wrongType.map((f) => f.name).join(', ')}`);
      }
      if (accepted.length === 0) return;

      const next = [...filesRef.current, ...accepted];
      // A controlled list belongs to the caller; announcing the new one is all this hook can do.
      if (!isControlled) {
        filesRef.current = next;
        setInternalFiles(next);
      }
      onFilesChange?.(next);
    },
    [disabled, allowedExtensions, maxSizeBytes, onFilesChange, onFileError, isControlled],
  );

  const removeFile = useCallback(
    (index: number) => {
      if (isControlled) {
        onRemove?.(index);
        return;
      }
      const next = filesRef.current.filter((_, i) => i !== index);
      filesRef.current = next;
      setInternalFiles(next);
      onFilesChange?.(next);
    },
    [isControlled, onRemove, onFilesChange],
  );

  const openPicker = useCallback(() => {
    if (disabled) return;
    inputRef.current?.click();
  }, [disabled]);

  return { files, addFiles, removeFile, openPicker, inputRef };
}
